import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiEye, FiLink2 } from "react-icons/fi";
import api from "../api";
import ImageGallery from "../components/ImageGallery";
import "./SharedAlbumPage.css";

/**
 * Public page for a shared album link (/album/:token).
 * Shows only the album's photos: no site header, no event nav, no path to the full gallery.
 * View-only links get watermarked images and no download controls.
 */
const SharedAlbumPage = () => {
  const { token } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [images, setImages] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | not_found

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/albums/shared/${encodeURIComponent(token)}`)
      .then((res) => {
        setData(res.data);
        setImages(res.data.images || []);
        setStatus("ready");
      })
      .catch(() => setStatus("not_found"));
  }, [token]);

  useEffect(() => {
    if (data?.album_name) document.title = `${data.album_name} · ${data.event.name}`;
  }, [data]);

  if (status === "loading") {
    return (
      <div className="sap-page sap-center">
        <div className="sap-spinner" />
        <span className="sap-muted">{t("shared_album.loading")}</span>
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <div className="sap-page sap-center">
        <div className="sap-empty-icon"><FiLink2 /></div>
        <h1 className="sap-empty-title">{t("shared_album.not_found_title")}</h1>
        <p className="sap-muted">{t("shared_album.not_found_desc")}</p>
        <Link to="/" className="sap-cta-btn">{t("shared_album.cta_btn")}</Link>
      </div>
    );
  }

  const { event, album_name, allow_download } = data;

  return (
    <div className="sap-page">
      <header className="sap-header">
        <div className="sap-event-name">{event.name}</div>
        <h1 className="sap-album-name">{album_name}</h1>
        <div className="sap-meta">
          <span>{t("shared_album.photo_count", { count: images.length })}</span>
          {!allow_download && (
            <span className="sap-badge"><FiEye /> {t("shared_album.view_only")}</span>
          )}
        </div>
      </header>

      <main className="sap-gallery">
        {images.length > 0 ? (
          <ImageGallery
            event={event}
            images={images}
            setImages={setImages}
            search={false}
            showInteractions={true}
            watermarkEnabled={!allow_download || event.watermark_enabled}
            allowDownload={allow_download}
            allowAlbums={false}
            allowPhotoShare={false}
          />
        ) : (
          <p className="sap-muted sap-center-text">{t("shared_album.empty")}</p>
        )}
      </main>

      <footer className="sap-footer">
        <span className="sap-muted">{t("shared_album.cta_text")}</span>
        <Link to="/" className="sap-cta-btn">{t("shared_album.cta_btn")}</Link>
      </footer>
    </div>
  );
};

export default SharedAlbumPage;
